// src/pages/RegistrationWorkflow.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { isActivatedComplete, useUser } from '../contexts/UserContext';
import { decodeJwt, isJwtExpired } from '../utilities/utilities';

import Stepper from '../components/Stepper';
import RegistrationForm from '../components/RegistrationForm';
import AccountRecovery from '../components/AccountRecovery';
import QualifyForm from '../components/QualifyForm';
import OTPVerification from '../components/OTPVerification';
import SetPassword from '../components/SetPassword';
import ContactForm from '../components/ContactForm';
import MedHistory from '../components/Medhistory';
import Consent from '../components/Consent';
import Welcome from '../components/Welcome';
import Spinner from '../components/Spinner';
import useModalA11y from '../hooks/useModalA11y';

import { api } from '../utilities/api'; 

const STEPS = [
  { id: 'qualify', title: 'Qualify', description: 'Find out if you qualify' },
  { id: 'register', title: 'Register', description: 'Register your account' },
  { id: 'otp', title: 'Verify', description: 'Verify your contact' },
  { id: 'password', title: 'Password', description: 'Choose your password' },
  { id: 'contact', title: 'Contact', description: 'Share your contact info' },
  { id: 'medhistory', title: 'History', description: 'Provide medical background' },
  { id: 'terms', title: 'Consent', description: 'Accept the terms' },
  { id: 'welcome', title: 'Welcome', description: 'Thank you for choosing RediMD!' },
];

const LOCAL_KEY_PREFIX = 'redimdwm_';
const registrationStorageKeys = [
  LOCAL_KEY_PREFIX+'registration_data',
  // Remove legacy verification artifacts left by an older client.
  LOCAL_KEY_PREFIX+'token',
  LOCAL_KEY_PREFIX+'registration_token',
]

const registrationAuthStorageKeys = [
  LOCAL_KEY_PREFIX+'userid',
  LOCAL_KEY_PREFIX+'user_name',
  LOCAL_KEY_PREFIX+'email',
  LOCAL_KEY_PREFIX+'firstname',
  LOCAL_KEY_PREFIX+'lastname',
  LOCAL_KEY_PREFIX+'activated',
  LOCAL_KEY_PREFIX+'jwt',
];

const clearRegistrationStorage = () => {
  const draft = readStoredRegistrationData();
  const storedUserId = localStorage.getItem(LOCAL_KEY_PREFIX+'userid');
  const storedActivated = localStorage.getItem(LOCAL_KEY_PREFIX+'activated');

  registrationStorageKeys.forEach(key => localStorage.removeItem(key));

  if (draft?.userid && String(draft.userid) === String(storedUserId) && !isActivatedComplete(storedActivated)) {
    registrationAuthStorageKeys.forEach(key => localStorage.removeItem(key));
  }
};

const REGISTRATION_STEP_ALIASES = {
  medhistory: 'medhistory',
  med_history: 'medhistory',
  setpassword: 'password',
};

const normalizeRegistrationStep = (stepId) => {
  const normalized = String(stepId || '').trim().toLowerCase();
  return REGISTRATION_STEP_ALIASES[normalized] || normalized;
};

const readStoredRegistrationData = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(LOCAL_KEY_PREFIX+'registration_data') || '{}');
    return parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0 ? parsed : null;
  } catch {
    localStorage.removeItem(LOCAL_KEY_PREFIX+'registration_data');
    return null;
  }
};

const hasRegistrationAuthority = (draft) => {
  if (draft?.capability) return true;

  const storedJwt = localStorage.getItem(LOCAL_KEY_PREFIX+'jwt');
  if (!storedJwt || isJwtExpired(storedJwt) || !draft?.userid) return false;

  const jwtSubject = decodeJwt(storedJwt)?.userid;
  return Boolean(jwtSubject && String(jwtSubject) === String(draft.userid));
};

const shouldPromptResume = (draft) => {
  if (!draft?.userid) return false;
  if (!hasRegistrationAuthority(draft)) return false;
  if (isActivatedComplete(draft.activated)) return false;
  return normalizeRegistrationStep(draft.registration_step) !== 'complete';
};

const draftOnlyFields = [
  'prefix', 'suffix', 'birthdate', 'gender', 'goal', 'goal_weight', 'height', 'weight',
  'user_name', 'login_method',
  'address1', 'address2', 'city', 'state', 'postal_code',
  'billing_address1', 'billing_address2', 'billing_city', 'billing_state', 'billing_postal_code',
  'phone_home', 'phone_work', 'phone_mobile', 'preferred_phone', 'preferred_contact',
  'personalHistory', 'familyHistory', 'personalHistoryOtherText', 'familyHistoryOtherText',
  'allergies', 'medications', 'hospitalizations',
  'is_hipaa_granted', 'personal_physician', 'physician_number',
  'additional_name', 'additional_email', 'additional_phone', 'is_opt_in', 'terms_accepted',
  // The opaque, purpose-scoped capability is required to resume preactivation
  // verification. It carries authority; userid is display/workflow state only.
  'capability',
];

const pickDraftOnlyFields = (draft = {}) => (
  draftOnlyFields.reduce((acc, field) => {
    if (draft[field] !== undefined) acc[field] = draft[field];
    return acc;
  }, {})
);

const normalizeHealthGoalFields = (data = {}) => ({
  ...data,
  goal_weight: data.goal_weight || data.goalWeight || data.goal || '',
  goal: data.goal || data.goal_weight || data.goalWeight || '',
  goal_date: data.goal_date || data.goalDate || '',
  start_date: data.start_date || data.startDate || '',
});

const hasRequiredHealthGoalFields = (data = {}) => (
  Number(data.height) > 0
  && Number(data.weight) > 0
  && Number(data.goal_weight || data.goal) > 0
);

function RegistrationWorkflow() {
  const { updateUserInfo } = useUser();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const resumeDialogRef = useRef(null);

  const step = normalizeRegistrationStep(searchParams.get('step') || location.state?.step);
  const capability = searchParams.get('capability') || location.state?.capability;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  // Owned HERE (not in RegistrationForm): setLoading(true) inside
  // RegistrationForm.handleSubmit is this component's setIsLoading, which
  // swaps RegistrationForm out for a Spinner (see renderStepComponent below)
  // WHILE the register() call is still in flight. If the 409 recovery token
  // were stored as RegistrationForm's own local state, it would be set on an
  // already-unmounted instance and silently dropped, then the subsequent
  // setLoading(false) would remount a fresh, empty RegistrationForm. Keeping
  // this state on the component that never unmounts fixes that.
  const [recoveryToken, setRecoveryToken] = useState(null);
  const [rejectMessage, setRejectMessage] = useState('');
  const [promptedResume, setPromptedResume] = useState(false);
  const [resumeChoice, setResumeChoice] = useState(null);
  const [workflowData, setWorkflowData] = useState(() => {
    if (step === 'otp' && capability) {
      return { capability };
    }
    return {};
  });

  const initialStep = (() => {
    if (step === 'otp' && capability) {
      return STEPS.findIndex(step => step.id === 'otp');
    }
    return 0;
  })();

  const [currentStepIndex, setCurrentStepIndex] = useState(initialStep);
  const currentStep = STEPS[currentStepIndex];
  const validResumeSteps = ['qualify', 'register', 'otp', 'password', 'contact', 'medhistory', 'terms'];

  useModalA11y({
    isOpen: promptedResume && resumeChoice === null,
    dialogRef: resumeDialogRef,
    closeOnEscape: false,
  });

  useEffect(() => {
    if (step !== 'otp' || !capability) return;
    const storedRegistrationData = readStoredRegistrationData();
    const capabilityMatchesStoredDraft = (
      storedRegistrationData?.capability
      && storedRegistrationData.capability === capability
    );
    const linkDraft = {
      ...(capabilityMatchesStoredDraft ? storedRegistrationData : {}),
      capability,
      registration_step: 'otp',
      activated: capabilityMatchesStoredDraft ? (storedRegistrationData?.activated || '0') : '0',
    };
    localStorage.setItem(LOCAL_KEY_PREFIX+'registration_data', JSON.stringify(linkDraft));
    setWorkflowData(prev => ({ ...prev, ...linkDraft }));
    setCurrentStepIndex(STEPS.findIndex(step => step.id === 'otp'));

    let cancelled = false;
    api.getRegistrationStep({ capability })
      .then(profile => {
        if (cancelled || profile?.error) return;
        setWorkflowData(prev => {
          const mergedDraft = normalizeHealthGoalFields({
            ...profile,
            ...pickDraftOnlyFields(prev),
            capability,
            registration_step: 'otp',
            activated: prev?.activated || profile?.activated || '0',
          });
          localStorage.setItem(LOCAL_KEY_PREFIX+'registration_data', JSON.stringify(mergedDraft));
          return mergedDraft;
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [capability, step]);

  // registration userid found in localStorage from incomplete registration ask user to continue aborted registration
	useEffect(() => {

    const storedRegistrationData = readStoredRegistrationData();
    if (!promptedResume && shouldPromptResume(storedRegistrationData) && !workflowData?.userid && !step) {
      setPromptedResume(true); 
    }

  }, [promptedResume, workflowData?.userid, step]);

	// user wants to contine registration, lets see where they left off and set the registration step
  useEffect(() => {
    if (!promptedResume || resumeChoice === null) return;

    const storedRegistrationData = readStoredRegistrationData();
    const storedUserId = storedRegistrationData?.userid;
    if (!storedUserId) return;

    if (resumeChoice) {
      setIsLoading(true);
      api.getRegistrationStep({ capability: storedRegistrationData?.capability })
        .then(profile => {
          if (!profile?.registration_step) throw new Error("Missing registration_step");
          const normalizedRegistrationStep = normalizeRegistrationStep(profile.registration_step_normalized || profile.registration_step);
          if (normalizedRegistrationStep === 'complete') {
            navigate('/login');
            return;
          }
          
					const currentIdx = STEPS.findIndex(s => s.id === normalizedRegistrationStep);

					let resumeIndex = currentIdx + 1;
					let nextStepId = STEPS[resumeIndex]?.id;
					while(!validResumeSteps.includes(nextStepId) && resumeIndex > 0){
            resumeIndex = resumeIndex - 1;
					  nextStepId = STEPS[resumeIndex]?.id;
          }

					if (!validResumeSteps.includes(nextStepId)) {
						clearRegistrationStorage();
						navigate('/register');
						return;
					}
					
          if (resumeIndex !== -1) {
						// if registration data was still in localStorage use it
							if (storedRegistrationData) {
								setWorkflowData(normalizeHealthGoalFields({
                  ...profile,
                  ...pickDraftOnlyFields(storedRegistrationData),
                  registration_step: normalizedRegistrationStep,
                }));
							} else {
								// otherwise use data from server
								setWorkflowData(normalizeHealthGoalFields({
                  ...profile,
                  registration_step: normalizedRegistrationStep,
                }));
							}
							setCurrentStepIndex(resumeIndex);
          } else {
            clearRegistrationStorage();
            navigate('/register');
          }
        })
        .catch(() => {
          clearRegistrationStorage();
          navigate('/register');
        })
        .finally(() => setIsLoading(false));
    } else {
      clearRegistrationStorage();
      navigate('/register');
    }
  }, [resumeChoice, promptedResume, navigate]);

  // useEffect(() => {
  //   if(!currentStep) return;

  //   if (currentStep?.id == 'otp' && workflowData?.userid && workflowData?.activated !== '1') {
  //     setIsLoading(true);
  //     api.getUserInfo(workflowData?.userid)
  //       .then(profile => {
  //         if (profile?.confirmed_email === '1') {
  //           setWorkflowData(prev => ({ ...prev, ...profile }));
  //         }
  //       })
  //       .finally(() => setIsLoading(false));
  //   }
  // }, [currentStep?.id, workflowData?.userid, workflowData?.activated]);

	// //update localstorage 
  // useEffect(() => {
  //   // no resume - no storage needed
  //   if (workflowData?.userid) {
  //     localStorage.setItem(LOCAL_KEY_PREFIX+'userid', workflowData?.userid || '');
  //     localStorage.setItem(LOCAL_KEY_PREFIX+'jwt', workflowData?.jwt || '');
  //     localStorage.setItem(LOCAL_KEY_PREFIX+'email', workflowData?.email || '');
  //     localStorage.setItem(LOCAL_KEY_PREFIX+'firstname', workflowData?.firstname || '');
  //     localStorage.setItem(LOCAL_KEY_PREFIX+'lastname', workflowData?.lastname || '');
  //     localStorage.setItem(LOCAL_KEY_PREFIX+'activated', workflowData?.activated || '0');
  //     localStorage.setItem(LOCAL_KEY_PREFIX+'registration_data', JSON.stringify(workflowData) || '');

  //     setUser({
  //       ...user,
  //       userid: workflowData?.userid || '',
  //       email: workflowData?.email || '',
  //       firstname: workflowData?.firstname || '',
  //       lastname: workflowData?.lastname || '',
  //       activated: workflowData?.activated || '' ,
  //     });      
  //   }
  // }, [workflowData, currentStepIndex]);

  // update workflowData without advancing to next step (resend otp in otpverification)
	const handleDataUpdate = (updatedData = {}) => {
    setWorkflowData(prevData => {
      const nextData = { ...prevData, ...updatedData };
      if (hasRegistrationAuthority(nextData)) {
        localStorage.setItem(LOCAL_KEY_PREFIX+'registration_data', JSON.stringify(nextData));
      }
      return nextData;
    });
  };

  // After every registration step.  update workflow data with latest data and advance to next step
	const handleStepCompletion = async (stepData = {}) => {
    setError('');
    const completedStepId = currentStep.id;
    const nextWorkflowData = normalizeHealthGoalFields({
      ...workflowData,
      ...stepData,
      registration_step: completedStepId,
    });
    setWorkflowData(nextWorkflowData);

    if (nextWorkflowData?.userid) {
      localStorage.setItem(LOCAL_KEY_PREFIX+'userid', nextWorkflowData?.userid || '');
      if (nextWorkflowData?.jwt) {
        localStorage.setItem(LOCAL_KEY_PREFIX+'jwt', nextWorkflowData.jwt);
      }
      localStorage.setItem(LOCAL_KEY_PREFIX+'user_name', nextWorkflowData?.user_name || nextWorkflowData?.email || '');
      localStorage.setItem(LOCAL_KEY_PREFIX+'email', nextWorkflowData?.email || '');
      localStorage.setItem(LOCAL_KEY_PREFIX+'firstname', nextWorkflowData?.firstname || nextWorkflowData?.first_name || '');
      localStorage.setItem(LOCAL_KEY_PREFIX+'lastname', nextWorkflowData?.lastname || nextWorkflowData?.last_name || '');
      localStorage.setItem(LOCAL_KEY_PREFIX+'activated', isActivatedComplete(nextWorkflowData?.activated) ? '1' : '0');
      localStorage.setItem(LOCAL_KEY_PREFIX+'registration_data', JSON.stringify(nextWorkflowData) || ''); 
    }

    let nextStepIndex = currentStepIndex + 1;
    if (completedStepId === 'otp') {
      nextStepIndex = STEPS.findIndex(s => s.id === 'password');
      if (location.search) {
        navigate(location.pathname, { replace: true });
      }
    }

    // advance to the next step unless this is the last step then update the users registration_step as complete
    if (nextStepIndex < STEPS.length - 1) {
      setCurrentStepIndex(nextStepIndex);
    } else {
      if (!hasRequiredHealthGoalFields(nextWorkflowData)) {
        setError('Current weight, goal weight, and height are required before registration can be completed.');
        setCurrentStepIndex(STEPS.findIndex(s => s.id === 'qualify'));
        return;
      }

      setIsLoading(true);
      try {
        const response = await updateUserInfo({
          registration_step : 'complete',
        });

        if (!response?.success) {
          throw new Error(response?.error || 'Failed to complete registration');
        }

        setCurrentStepIndex(nextStepIndex);
      } catch (err) {
        setError(err.message || 'Failed to complete registration. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

	// navigate to previous step, when possible
	const handleBack = () => {
    if (currentStepIndex === 0) return;
    const prevStepId = STEPS[currentStepIndex - 1].id;
    if (['welcome'].includes(prevStepId)) return;
    setCurrentStepIndex(prev => prev - 1);
  };

  const renderStepComponent = () => {
    switch (currentStep.id) {
      case 'qualify':
        return <QualifyForm onSuccess={handleStepCompletion} setLoading={setIsLoading} setMessage={setMessage} setError={setError} initialData={workflowData} />;
      case 'register':
        return (
          <RegistrationForm
            onSuccess={handleStepCompletion}
            setLoading={setIsLoading}
            setMessage={setMessage}
            setError={setError}
            initialData={workflowData}
            onAccountRecovery={(token, msg) => { setRecoveryToken(token); setRejectMessage(msg); }}
          />
        );
      case 'otp':
        return <OTPVerification onSuccess={handleStepCompletion} onDataUpdate={handleDataUpdate} setLoading={setIsLoading} setMessage={setMessage} setError={setError} initialData={workflowData} />;
      case 'password':
        return <SetPassword onSuccess={handleStepCompletion} setLoading={setIsLoading} setMessage={setMessage} setError={setError} initialData={workflowData} />;
      case 'contact':
        return <ContactForm onSuccess={handleStepCompletion} setLoading={setIsLoading} setMessage={setMessage} setError={setError} initialData={workflowData} isWorkflowStep={true} />;
      case 'medhistory':
        return <MedHistory onSuccess={handleStepCompletion} parentSetLoading={setIsLoading} parentSetError={setError} initialData={workflowData} isWorkflowStep={true} />;
      case 'terms':
        return <Consent onSuccess={handleStepCompletion} setLoading={setIsLoading} setMessage={setMessage} setError={setError} initialData={workflowData}  isWorkflowStep={true} />;
      case 'welcome':
        return <Welcome />;
      default:
        return <div>Error loading step</div>;
    }
  };

  return (
    <>
      <div className="w-full max-w-6xl px-4 mb-2 md:mb-6 mt-0">
        <Stepper currentStep={currentStepIndex} totalSteps={STEPS.length} stepLabels={STEPS.map(step => step.title)} />
      </div>

      {promptedResume && resumeChoice === null && (
        <div id="resumeModal" className="my-0 fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            ref={resumeDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="resumeRegistrationTitle"
            tabIndex={-1}
            className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
          >
            <h2 id="resumeRegistrationTitle" className="text-2xl font-semibold mb-4 text-primary-dark">Continue Registration</h2>
            <p className="mb-4 text-gray-700">An incomplete registration was found. Would you like to continue?</p>
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button type="button" onClick={() => setResumeChoice(false)} className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400">Start Over</button>
              <button type="button" onClick={() => setResumeChoice(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Resume Registration</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row flex-grow w-full max-w-4xl p-2 md:p-6 bg-white rounded-lg md:shadow-lg mb-8 mx-auto">
        <div className="hidden lg:block lg:w-1/4 pr-4 border-r border-neutral-dark">
          <h2 className="text-xl font-semibold text-primary mb-4">{currentStep.title}</h2>
          <p className="text-primary-dark">{currentStep.description}</p>
        </div>

        <div className="w-full md:w-3/4 md:pl-4 flex flex-col relative">
          {error && (
            <div className="mb-4 p-2 bg-red-100 text-red-700 rounded">
              <p className="font-bold">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          )}

          {message && (
            <div className="mb-4 p-2 bg-green-100 text-green-700 rounded">
              <p className="font-bold">Message</p>
              <p className="text-sm">{message}</p>
            </div>
          )}

          {recoveryToken ? (
            <div className="flex-grow mb-6">
              <AccountRecovery recoveryToken={recoveryToken} rejectMessage={rejectMessage} />
            </div>
          ) : isLoading ? (
            <div className="flex justify-center items-center h-full">
              <Spinner />
            </div>
          ) : (
            <div className="flex-grow mb-6">{renderStepComponent()}</div>
          )}

          {!recoveryToken && currentStepIndex > 0 && !['password', 'welcome'].includes(currentStep.id) && (
            <button
              onClick={handleBack}
              className="mt-4 self-start bg-neutral-dark text-white px-4 py-2 rounded hover:bg-primary-dark transition duration-200"
            >
              Back
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export default RegistrationWorkflow;
